import { MutationOptions } from '@apollo/client';
import { Skeleton, Stack } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import * as _ from 'lodash';
import React from 'react';

import { utils as commonUtils } from '@finley-technologies/common';

import {
  GetCurrentFundingRequestDocumentGQLType,
  GetFundingRequestDocumentGQLType,
  GetFundingRequestWithBaseDetailsDocumentGQLType,
  UserApprovalGroupsForFundingRequestDocumentGQLType,
  useRequestApprovalForFundingRequestMutation,
  useUserApprovalActionForFundingRequestWorkflowInstanceMutation,
  useUserApprovalGroupsForFundingRequestQuery,
} from '../../../../../../types/dashboard-core-gqlTypes.generated.d';
import { colors } from 'packages/common-frontend/colors';
import { Typography } from 'packages/common-frontend/components/Typography';
import { InternalApprovalInstance } from 'packages/workflows/ui/InternalApprovalInstance';

export const FundingRequestInternalApproval = ({
  fundingRequestId,
  workflowInstanceId,
  creditFacilityId,
  creditFacilityName,
  readOnly = false,
}: {
  fundingRequestId: string;
  workflowInstanceId: string;
  creditFacilityId: string;
  creditFacilityName: string;
  readOnly?: boolean;
}) => {
  const userApprovalGroupsQuery = useUserApprovalGroupsForFundingRequestQuery({
    fetchPolicy: 'network-only',
    variables: { fundingRequestId },
  });
  if (userApprovalGroupsQuery.error) {
    Sentry.captureException(userApprovalGroupsQuery.error);
    throw Error(
      `Failed to get user approvals for funding request. Contact Finley Support.`,
    );
  }
  const userApprovalQueries: MutationOptions['refetchQueries'] = [
    {
      query: UserApprovalGroupsForFundingRequestDocumentGQLType,
      variables: { fundingRequestId },
    },
  ];

  const [
    userApprovalActionForFundingRequestWorkflowInstanceMutation,
    userApprovalActionMutation,
  ] = useUserApprovalActionForFundingRequestWorkflowInstanceMutation({
    awaitRefetchQueries: true,
    refetchQueries: [
      ...userApprovalQueries,
      {
        query: GetFundingRequestWithBaseDetailsDocumentGQLType,
        variables: { fundingRequestId },
      },
      {
        query: GetFundingRequestDocumentGQLType,
        variables: { fundingRequestId },
      },
      {
        query: GetCurrentFundingRequestDocumentGQLType,
        variables: { creditFacilityId },
      },
    ],
  });

  if (userApprovalActionMutation.error) {
    if (commonUtils.EnvUtils.isCustomerFacing()) {
      Sentry.captureException(userApprovalActionMutation.error);
      throw Error(
        `Failed to execute user action for funding request. Contact Finley Support.`,
      );
    } else {
      throw userApprovalActionMutation.error;
    }
  }

  const [
    requestApproval,
    requestApprovalMutation,
  ] = useRequestApprovalForFundingRequestMutation({
    refetchQueries: [...userApprovalQueries],
    awaitRefetchQueries: true,
  });

  if (requestApprovalMutation.error) {
    if (commonUtils.EnvUtils.isCustomerFacing()) {
      Sentry.captureException(requestApprovalMutation.error);
      throw Error(
        `Failed request approval for funding request. Contact Finley Support.`,
      );
    } else {
      throw requestApprovalMutation.error;
    }
  }

  if (!userApprovalGroupsQuery.data) {
    return <Skeleton h="400px" />;
  }

  return (
    <Stack
      w="100%"
      justifyContent="flex-start"
      p="40px"
      border={`1px solid ${colors.gray[200]}`}
      borderRadius="8px"
    >
      <Typography variant="section-title" fontWeight="bold">
        Approve Funding Request
      </Typography>

      <Stack gap="3rem">
        <Typography variant="detail">
          Approval from the following users is required in sequence before a
          Funding Request can be approved.
        </Typography>
        <InternalApprovalInstance
          readOnly={readOnly}
          approvalGroups={
            userApprovalGroupsQuery.data.userApprovalGroupsForFundingRequest
          }
          approveParams={{
            confirmationTitle: 'Confirm Approval',
            confirmationText: `Are you sure you would like to approve this funding request for ${_.startCase(
              creditFacilityName,
            )}?`,
            onPerformAction: async (params: { userId: string }) => {
              if (readOnly) {
                return;
              }
              await userApprovalActionForFundingRequestWorkflowInstanceMutation(
                {
                  variables: {
                    action: 'approve',
                    fundingRequestId,
                    workflowInstanceId,
                    userId: params.userId,
                  },
                },
              );
            },
          }}
          rejectParams={{
            confirmationTitle: 'Confirm Rejection',
            confirmationText: `Are you sure you would like to reject this funding request for ${_.startCase(
              creditFacilityName,
            )}?`,
            onPerformAction: async (params: { userId: string }) => {
              if (readOnly) {
                return;
              }
              await userApprovalActionForFundingRequestWorkflowInstanceMutation(
                {
                  variables: {
                    action: 'reject',
                    fundingRequestId,
                    workflowInstanceId,
                    userId: params.userId,
                  },
                },
              );
            },
          }}
          requestApprovalParams={{
            confirmationTitle: 'Confirm Request Approval',
            confirmationText: `Are you sure you would like to request approval on this funding request for ${_.startCase(
              creditFacilityName,
            )}?`,
            onPerformAction: async ({ userId }) => {
              if (readOnly) {
                return;
              }
              await requestApproval({
                variables: {
                  fundingRequestId,
                  workflowInstanceId,
                  approverUserId: userId,
                },
              });
            },
          }}
        />
      </Stack>
    </Stack>
  );
};
